import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { IState, ReduxMap } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';
import Icon, { IconType } from '../Icon/Icon';
import './EntryIdManagerPage.css';

type EntryIdRow = {
  projectIntId: number;
  projectKey: string;
  displayId: string;
  fixedId: string | null;
  title: string;
  category: string;
  students: string;
  school: string | null;
  status: string | null;
};

type EntryIdListValue = {
  entries: EntryIdRow[];
};

type SetEntryIdValue = {
  displayId: string;
  fixedId: string | null;
};

const EntryIdManagerPage = (_props: IState) => {
  const [entries, setEntries] = useState<EntryIdRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [search, setSearch] = useState('');
  const [denied, setDenied] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [renumbering, setRenumbering] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = () => {
    store.server.postApi<ServerResponse<EntryIdListValue>>('../EntryId/List', {}).then((x) => {
      if (x.Success) {
        const list = x.Value?.entries ?? [];
        setEntries(list);
        const next: Record<number, string> = {};
        list.forEach((e) => {
          next[e.projectIntId] = e.fixedId ?? '';
        });
        setDrafts(next);
        setDenied(false);
        setDeniedMessage('');
      } else {
        setDenied(true);
        setDeniedMessage(x.Message || 'This page is not enabled for this event.');
        toast.error(x.Message, { autoClose: false });
      }
      setLoaded(true);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const hay = [e.displayId, e.fixedId, e.title, e.students, e.category, e.school, e.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.indexOf(q) >= 0;
    });
  }, [entries, search]);

  const hasFixedIds = useMemo(
    () => entries.some((e) => (e.fixedId ?? '').trim() !== ''),
    [entries],
  );

  const saveRow = (row: EntryIdRow) => {
    const value = (drafts[row.projectIntId] ?? '').trim();
    setSavingId(row.projectIntId);
    store.server
      .postApi<ServerResponse<SetEntryIdValue>>('../EntryId/SetEntryId', {
        projectIntId: row.projectIntId,
        projectId: value,
      })
      .then((x) => {
        setSavingId(null);
        if (x.Success) {
          toast.success(`Saved ${x.Value?.displayId || value || row.displayId}.`);
          setEntries((prev) =>
            prev.map((e) =>
              e.projectIntId === row.projectIntId
                ? {
                    ...e,
                    displayId: x.Value?.displayId || value || e.displayId,
                    fixedId: x.Value?.fixedId ?? (value || null),
                  }
                : e,
            ),
          );
          setDrafts((prev) => ({
            ...prev,
            [row.projectIntId]: x.Value?.fixedId ?? value,
          }));
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      });
  };

  const runRenumber = (url: string, confirmMessage: string) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setRenumbering(true);
    store.server.postApi<ServerResponse<boolean>>(url, {}).then((x) => {
      setRenumbering(false);
      if (x.Success) {
        toast.success('Projects renumbered.');
        load();
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const renumber = () => {
    runRenumber(
      '../EntryId/RenumberProjects',
      'Renumber all projects? This recalculates automatic entry numbers. Entries with a Fixed Id keep that id.',
    );
  };

  const renumberByTeamLeader = () => {
    runRenumber(
      '../EntryId/RenumberProjectsByTeamLeader',
      'Renumber all projects by team leader last name? This recalculates automatic entry numbers in last-name order of each team leader. Entries with a Fixed Id keep that id.',
    );
  };

  const clearAllFixedIds = () => {
    if (
      !window.confirm(
        'Clear all Fixed Ids? Entries will go back to their generated ids. This cannot be undone except by setting Fixed Ids again.',
      )
    ) {
      return;
    }
    setRenumbering(true);
    store.server.postApi<ServerResponse<number>>('../EntryId/ClearAllFixedIds', {}).then((x) => {
      setRenumbering(false);
      if (x.Success) {
        const n = x.Value ?? 0;
        toast.success(n === 1 ? 'Cleared 1 Fixed Id.' : `Cleared ${n} Fixed Ids.`);
        load();
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  if (loaded && denied) {
    return (
      <div className="main-body-page" data-testid="entry-id-manager-page">
        <div className="alert alert-warning">{deniedMessage || 'This page is not enabled for this event.'}</div>
      </div>
    );
  }

  return (
    <div className="main-body-page entry-id-manager" data-testid="entry-id-manager-page">
      <div className="flex-between wrap bumper-l">
        <div>
          <h2>
            <Icon type={IconType.idBadge} /> Entry IDs
          </h2>
          <p className="text-muted">
            Set a Fixed Id to override the automatic entry id. Clear the Fixed Id and save to go back to the generated
            id. Clear All Fixed Ids removes every override at once. Renumber updates automatic numbers only. Renumber All
            by Team Leader assigns numbers in team-leader last-name order.
          </p>
        </div>
        <div className="entry-id-manager-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={renumber}
            disabled={renumbering || !loaded}
            data-testid="renumber-projects"
          >
            <Icon type={IconType.refresh} /> Renumber All Projects
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={renumberByTeamLeader}
            disabled={renumbering || !loaded}
            data-testid="renumber-projects-by-team-leader"
          >
            <Icon type={IconType.childTeamLeader} /> Renumber All by Team Leader
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={clearAllFixedIds}
            disabled={renumbering || !loaded || !hasFixedIds}
            data-testid="clear-all-fixed-ids"
          >
            <Icon type={IconType.delete} /> Clear All Fixed Ids
          </button>
        </div>
      </div>

      <div className="flex-between wrap bumper-l">
        <input
          type="text"
          className="form-control entry-id-manager-search"
          placeholder="Search entries"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="entry-id-search"
        />
        <div className="text-muted">
          {filtered.length} of {entries.length}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Entry Id</th>
              <th>Fixed Id</th>
              <th>Title</th>
              <th>Student(s)</th>
              <th>Category</th>
              <th>School</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.projectIntId} data-testid={`entry-id-row-${row.projectIntId}`}>
                <td>
                  <strong>{row.displayId}</strong>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={20}
                    value={drafts[row.projectIntId] ?? ''}
                    aria-label={`Fixed id for ${row.displayId || row.title}`}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [row.projectIntId]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveRow(row);
                      }
                    }}
                  />
                </td>
                <td>{row.title}</td>
                <td>{row.students}</td>
                <td>{row.category}</td>
                <td>{row.school}</td>
                <td className="text-right">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => saveRow(row)}
                    disabled={savingId === row.projectIntId}
                  >
                    <Icon type={IconType.save} /> Save
                  </button>
                </td>
              </tr>
            ))}
            {loaded && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No entries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReduxMap(EntryIdManagerPage);
